import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Screen, ScreenTitle, PrimaryButton } from '../ui/Screen.jsx'
import { useConfigBundle, screensOf } from '../config.js'
import { NavBack } from '../ui/NavBar.jsx'

import {
  PLATFORM,
  detectPlatform,
  isStandalone,
  isIosSafari,
  canPromptInstall,
  promptInstall,
  onInstallStateChange,
} from '../pwa.js'

/**
 * Step 2 of 3: add to the home screen.
 *
 * WHAT THIS SCREEN IS FOR HAS CHANGED (DS-6 §0.1). It was written believing its
 * job was push: on iOS Web Push does not exist until the app is on the home
 * screen, so skipping meant five silent days. That is still true as a
 * capability — and it has never mattered once, because push has ZERO observed
 * deliveries on any platform. The screen led on a benefit nobody has ever
 * received.
 *
 * Its honest job today is RE-ENTRY. She comes back to this app about thirty
 * times in five days, and an icon is a working route back where a browser tab
 * she has to find again is not. That benefit needs no channel to work and is
 * the only one that has ever been delivered.
 *
 * Push remains the intended architecture and this screen will matter enormously
 * the moment it works, which is why the design is reordered and not rebuilt.
 *
 * Android/Chromium gets the real install dialog when the browser offers one.
 * iOS cannot be prompted at all — Add to Home Screen is manual, by design — so
 * it gets steps that name the actual buttons.
 */
export default function Install() {
  const navigate = useNavigate()
  const [platform] = useState(() => detectPlatform())
  const [installed, setInstalled] = useState(() => isStandalone())
  const [promptable, setPromptable] = useState(() => canPromptInstall())
  const { data: bundle } = useConfigBundle()
  const copy = screensOf(bundle)

  useEffect(() => onInstallStateChange(() => setPromptable(canPromptInstall())), [])

  useEffect(() => {
    const media = window.matchMedia?.('(display-mode: standalone)')
    if (!media) return undefined
    const onChange = () => setInstalled(isStandalone())
    media.addEventListener?.('change', onChange)
    return () => media.removeEventListener?.('change', onChange)
  }, [])

  /*
   * The channel passes straight through. Install does not use it; the
   * permission step does, and this is the only route between demographics and
   * there.
   */
  const { state } = useLocation()
  /*
   * The only way off this screen, and it is reachable only from the INSTALLED
   * state. Under A2 there is no self-report and no skip: the media query is the
   * evidence and nothing here can assert past it.
   */
  const next = () =>
    navigate('/notifications', { replace: true, state: { notifChannel: state?.notifChannel ?? null } })

  async function install() {
    const accepted = await promptInstall()
    if (accepted) setInstalled(true)
  }

  if (installed) {
    return (
      <Screen disposition="status" nav={{ left: <NavBack />, right: 'Step 2 of 3' }} action={<PrimaryButton onClick={next}>Continue</PrimaryButton>}>
        <ScreenTitle subtitle="Step 2 of 3">You&rsquo;re all set up</ScreenTitle>
        <p className="text-base leading-relaxed text-[var(--ink-700)]">
          Editrics is on your home screen. Open it from there whenever you have a moment,
          and it will show you if a check-in is waiting.
        </p>
      </Screen>
    )
  }

  return (
    /*
     * §4.3 — THE ACTION IS IN THE BAND, NEVER INLINE IN THE BODY.
     *
     * `Add to home screen` was rendered inline, and wrapped in `promptable` —
     * which is false on every iOS device, because iOS has no install prompt to
     * fire. So on the platform this screen exists for, the block rendered
     * nothing and the screen had no action region at all.
     *
     * In the band it is a real primary where one exists. Where it does not —
     * iOS, and any browser that never fires beforeinstallprompt — no action is
     * passed and Screen renders NO BAND rather than an empty one. That is
     * honest: on iOS the step is not completed by pressing anything here, it is
     * completed by opening the app from the new icon, which the body says.
     */
    <Screen
      disposition="status"
      nav={{ left: <NavBack />, right: 'Step 2 of 3' }}
      action={promptable ? <PrimaryButton onClick={install}>Add to home screen</PrimaryButton> : null}
    >
      <ScreenTitle>Add Editrics to your home screen</ScreenTitle>

      {/*
        RE-ENTRY LEADS — DS-6 §0.1, and the reason is worth keeping in the file.

        This screen was written believing its job was push. Push has zero
        observed deliveries, ever, so the benefit it led on had never once been
        delivered to anyone. Design's correction: install has a SECOND benefit,
        it is independent of push, and it is the only one that is true today —
        it is how she gets back in. She re-enters this app about thirty times in
        five days; an icon is a working, observable route back and a browser tab
        she has to find again is not.

        So the reasons are REORDERED, not rewritten, and the screen is
        deliberately NOT weighted up to compensate. The reflex when a channel
        fails is to press harder on the step meant to fix it, which would be
        persuading her more strongly toward a benefit never delivered. Same
        weight, better reason.

        The reminder benefit stays, phrased as what installing ENABLES and never
        as what it achieves.
      */}
      <p className="mb-5 text-base leading-relaxed text-[var(--ink-700)]">
        {copy?.install?.re_entry ??
          'Adding Editrics to your home screen gives you an icon to open. You will be coming back to it six times a day for five days.'}
      </p>
      {platform === PLATFORM.IOS ? (
        <p className="mb-5 text-base leading-relaxed text-[var(--ink-700)]">
          {copy?.install?.reminders_enabled ??
            'On iPhone, reminders can only work once Editrics is on your home screen.'}
        </p>
      ) : null}

      <Instructions platform={platform} />

      {/*
        A2 — NO SELF-REPORT AND NO SKIP.
        "I've done this" and "Skip for now" both let her past an app that was
        never installed, on her word rather than on evidence. The media query is
        the evidence, and it flips by itself the moment she adds the icon, so
        this screen needs no way to assert progress it can simply observe.
        A1 (display-mode: standalone) is what makes removing them safe.
      */}
      {/*
        ACCURATE ON BOTH PLATFORMS, which the obvious wording is not.
        "This screen moves on by itself" is true on Android, where the installed
        window is this window and the media query flips under her. On iOS it is
        FALSE: Add to Home Screen creates a SEPARATE standalone instance and the
        Safari tab she is reading this in never becomes standalone, so it would
        sit there waiting for a change that cannot arrive in that tab. So the
        instruction names the act that actually completes the step.
      */}
      <p className="mt-6 text-sm leading-relaxed text-[var(--ink-500)]">
        Once you&rsquo;ve added it, open Editrics from the new icon - this step finishes there.
      </p>

      {/*
        The skip's cost was "reminders may not reach me" — which asserts, in the
        negative, that installing IS what makes them reach her. Push has never
        delivered, so that cost was the same unearned claim wearing a warning's
        clothes, and it was doing persuasive work the evidence does not support.

        Stated as what skipping actually means instead. Still quiet, still
        findable, still not weighted up (§0.1).
      */}
    </Screen>
  )
}

function Instructions({ platform }) {
  if (platform === PLATFORM.IOS) {
    return (
      <Steps
        note={
          isIosSafari()
            ? null
            : 'On iPhone and iPad, only Safari can add an app to the home screen. Open this page in Safari first.'
        }
        steps={[
          <>
            Tap the <strong>Share</strong> button at the bottom of the screen - the square with an
            arrow pointing up.
          </>,
          <>
            Scroll down the list and tap <strong>Add to Home Screen</strong>.
          </>,
          <>
            Tap <strong>Add</strong> in the top right.
          </>,
          <>Open Editrics from your home screen from now on.</>,
        ]}
      />
    )
  }

  if (platform === PLATFORM.ANDROID) {
    return (
      <Steps
        steps={[
          <>
            Tap the <strong>menu</strong> button - the three dots in the top right.
          </>,
          <>
            Tap <strong>Add to Home screen</strong>, or <strong>Install app</strong> if you see it.
          </>,
          <>
            Confirm with <strong>Add</strong> or <strong>Install</strong>.
          </>,
          <>Open Editrics from your home screen from now on.</>,
        ]}
      />
    )
  }

  return (
    <Steps
      note="You're on a computer. Editrics runs on your phone, so open this page there and add it to your home screen."
      steps={[
        <>Open this page on the phone you&rsquo;ll be using.</>,
        <>Follow the instructions it shows you there.</>,
      ]}
    />
  )
}

function Steps({ steps, note }) {
  return (
    <div>
      {note ? (
        <p className="mb-4 rounded-[var(--r-surface)] bg-[var(--surface-sunken)] px-4 py-3 text-sm text-[var(--ink-900)]">{note}</p>
      ) : null}
      <ol className="flex flex-col gap-3">
        {steps.map((step, i) => (
          <li key={i} className="flex gap-3">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--r-pill)] bg-[var(--surface-sunken)] text-sm font-semibold tabular-nums text-[var(--ink-700)]">
              {i + 1}
            </span>
            <span className="pt-0.5 text-base leading-snug text-[var(--ink-900)]">{step}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}
